import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  downloadSowFileProps,
  SowListItem,
  SowState,
} from '../../../../../types/ProjectManagement/Project/ProjectView/SOW/sowTypes'
import { LoadingState, ValidationError } from '../../../../../types/commonTypes'
import sowApi from '../../../../../middleware/api/ProjectManagement/Projects/ProjectView/SOW/sowApi'
import { ApiLoadingState } from '../../../../../middleware/api/apiList'
import { RootState } from '../../../../../stateStore'

export const initialSowState: SowState = {
  sowList: [],
  isLoading: ApiLoadingState.idle,
  downloadedBlob: null,
  listSize: 0,
  isLoadingUpload: false,
}

export const uploadProjectSOW = createAsyncThunk(
  '/project-mgmt/uploadProjectSOW',
  async (payload, thunkApi) => {
    try {
      return await sowApi.uploadProjectSOW
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const downloadSOWFile = createAsyncThunk(
  '/project-mgmt/downloadSOWFile',
  async (data: downloadSowFileProps, thunkApi) => {
    try {
      return await sowApi.downloadSOWFile(data.projectId, data.sowId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const getSowFileData = createAsyncThunk(
  '/project-mgmt/getSOWFileData',
  async (payload, thunkApi) => {
    try {
      return await sowApi.getSowFileData
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const sowSlice = createSlice({
  name: 'projectViewSow',
  initialState: initialSowState,
  reducers: {
    setIsLoadingUpload: (state, action): void => {
      state.isLoadingUpload = action.payload
    },
    setProjectId: (state, action): void => {
      state.projectId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadSOWFile.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(downloadSOWFile.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(downloadSOWFile.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getSowFileData.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getSowFileData.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getSowFileData.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(uploadProjectSOW.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(uploadProjectSOW.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(uploadProjectSOW.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
  },
})

// Root selectors (typed via RootState with safe feature access)
const selectFeature = (state: RootState): SowState => {
  const root = state as unknown as { projectSow: SowState }
  return root.projectSow ?? initialSowState
}

const isLoading = (state: RootState): LoadingState =>
  state.SowFileData.isLoading
const sowList = (state: RootState): SowListItem[] =>
  selectFeature(state).sowList
const listSize = (state: RootState): number => selectFeature(state).listSize
const downloadedBlob = (state: RootState): Blob | null | undefined =>
  selectFeature(state).downloadedBlob
const projectId = (state: RootState): number | undefined =>
  selectFeature(state).projectId
const isLoadingUpload = (state: RootState): boolean =>
  selectFeature(state).isLoadingUpload

const sowThunks = {
  uploadProjectSOW,
  downloadSOWFile,
  getSowFileData,
}

const sowSelectors = {
  isLoading,
  sowList,
  listSize,
  downloadedBlob,
  projectId,
  isLoadingUpload,
}

export const sowService = {
  ...sowThunks,
  actions: sowSlice.actions,
  selectors: sowSelectors,
}

export default sowSlice.reducer
